import {useMutation, useQueryClient} from '@tanstack/react-query';
import {
  addFavorite,
  addFavoriteAlarm,
  deleteFavorite,
  deleteFavoriteAlarm,
} from '@/api/channel/board';
import {BOARD_QUERY_KEY} from './queryKeys';
import {AddFavoriteModel} from '@/types/channel/board/board.model';
import {STATION_QUERY_KEY} from '../station/queryKeys';
import {StationModel} from '@/types/channel/station/station.model';
import {isAxiosError} from 'axios';
import throwToast from '@/utils/ui/toast';
import {ChannelT} from '@/utils/i18n/channel';

export const useFavoriteMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: AddFavoriteModel) => {
      return addFavorite({bjId: params.streamerId});
    },
    onSuccess: (data, params) => {
      queryClient.invalidateQueries({
        queryKey: BOARD_QUERY_KEY.POST_LIST(params.streamerId),
      });

      queryClient.setQueryData(
        STATION_QUERY_KEY.STATION_INFO(params.streamerId),
        (old?: StationModel) => {
          return {
            ...old,
            userPermissions: {
              ...old?.userPermissions,
              isFavorite: !(old?.userPermissions?.isFavorite ?? false),
            },
          };
        },
      );

      queryClient.setQueryData(
        BOARD_QUERY_KEY.FAVORITE(params.streamerId),
        (old: {isFavorite: boolean} | undefined) => ({
          isFavorite: !(old?.isFavorite ?? false),
        }),
      );
      throwToast(data?.message);
    },
    onError(error, variables, context) {
      if (isAxiosError(error)) {
        if (error.response?.data.code === 6306) {
          throwToast(
            ChannelT(
              '1일 최대 즐겨찾기 추가 횟수를 초과하였습니다. 내일 다시 시도해주세요.',
            ),
          );
        } else {
          throwToast(
            error.response?.data?.message || ChannelT('오류가 발생했습니다.'),
          );
        }
      }
    },
  });
};

export const useDeleteFavoriteMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: {userId: string}) => {
      return deleteFavorite({userId: params.userId});
    },
    onSuccess: (data, params) => {
      queryClient.invalidateQueries({
        queryKey: BOARD_QUERY_KEY.POST_LIST(params.userId),
      });
      queryClient.setQueryData(
        BOARD_QUERY_KEY.FAVORITE(params.userId),
        (old: {isFavorite: boolean} | undefined) => ({
          isFavorite: !(old?.isFavorite ?? false),
        }),
      );
      queryClient.setQueryData(
        STATION_QUERY_KEY.STATION_INFO(params.userId),
        (old?: StationModel) => {
          return {
            ...old,
            userPermissions: {
              ...old?.userPermissions,
              isFavorite: !(old?.userPermissions?.isFavorite ?? false),
            },
          };
        },
      );
      throwToast(data?.message);
    },
    onError(error, variables, context) {
      if (isAxiosError(error)) {
        throwToast(
          error.response?.data?.message || ChannelT('오류가 발생했습니다.'),
        );
      }
    },
  });
};

export const useAddFavoriteAlarmMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: {userId: string}) => {
      return addFavoriteAlarm({userId: params.userId});
    },
    onSuccess: (_, params) => {
      queryClient.setQueryData(
        STATION_QUERY_KEY.STATION_INFO(params.userId),
        (old?: StationModel) => {
          return {
            ...old,
            userPermissions: {
              ...old?.userPermissions,
              isMobilePushEnabled: !(
                old?.userPermissions?.isMobilePushEnabled ?? false
              ),
            },
          };
        },
      );
    },
    onError(error, variables, context) {
      if (isAxiosError(error)) {
        throwToast(
          error.response?.data?.message || ChannelT('오류가 발생했습니다.'),
        );
      }
    },
  });
};

export const useDeleteFavoriteAlarmMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: {userId: string}) => {
      return deleteFavoriteAlarm({userId: params.userId});
    },
    onSuccess: (_, params) => {
      queryClient.setQueryData(
        STATION_QUERY_KEY.STATION_INFO(params.userId),
        (old?: StationModel) => {
          return {
            ...old,
            userPermissions: {
              ...old?.userPermissions,
              isMobilePushEnabled: !(
                old?.userPermissions?.isMobilePushEnabled ?? false
              ),
            },
          };
        },
      );
    },
    onError(error, variables, context) {
      if (isAxiosError(error)) {
        throwToast(
          error.response?.data?.message || ChannelT('오류가 발생했습니다.'),
        );
      }
    },
  });
};
